import { approvalsNotificationListMaxLimit } from '@gonfalon/dogfood-flags/dogfood-flags';
import { pluralize } from '@gonfalon/format/pluralize';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import type { RefinementCtx } from 'zod';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import { z } from 'zod';

import { isValidResourceConfirmation } from '#utils/confirmationUtils';

// Zod schema for PendingChangesFormRecord
export const pendingChangesFormSchema = z.object({
  isScheduleSelected: z.boolean(),
  bypassRequiredApprovals: z.boolean(),

  executionDate: z.number(),
  reviewers: z.array(
    z.object({
      id: z.string(),
      type: z.enum(['member', 'team']),
    }),
  ),
  acknowledgeConflicts: z.boolean(),
  resourceConfirmation: z.string(),
  comment: z.string(),
  integrationConfig: z.record(z.string(), z.union([z.string(), z.boolean(), z.number(), z.undefined()])),
});

export const pendingChangesValidation = (
  data: PendingChangesFormSchema,
  ctx: RefinementCtx,
  resourceInfo: ResourceInfo,
  formRequirements: FormRequirements,
) => {
  if (data.isScheduleSelected) {
    if (data.executionDate <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_date,
        path: ['executionDate'],
        message: 'Execution date must be in the future',
      });
    }

    if (data.executionDate <= new Date().getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_date,
        path: ['executionDate'],
        message: 'Execution date must be in the future',
      });
    }
  }
  const isApprovalRequired = data.bypassRequiredApprovals ? false : formRequirements.isApprovalRequired;
  const isApprovalRequestedOrRequired = isApprovalRequired || data.reviewers.length > 0;

  // Validate reviewer count dynamically
  if (isApprovalRequired && formRequirements.usesLaunchDarklyApprovalService && data.reviewers.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      path: ['reviewers'],
      minimum: formRequirements.minNumberOfApprovals,
      inclusive: true,
      type: 'array',
      message: `At least ${formRequirements.minNumberOfApprovals} ${pluralize(formRequirements.minNumberOfApprovals, 'reviewer is', 'reviewers are')} required to approve your changes.`,
    });
  }

  if (approvalsNotificationListMaxLimit() < data.reviewers.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_big,
      path: ['reviewers'],
      maximum: approvalsNotificationListMaxLimit(),
      inclusive: false,
      type: 'number',
      message: `You may only specify at most ${approvalsNotificationListMaxLimit()} reviewers`,
    });
  }
  const isCommentRequired = formRequirements.isCommentRequired || isApprovalRequestedOrRequired;

  if (isCommentRequired && data.comment.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      path: ['comment'],
      minimum: 1,
      inclusive: true,
      type: 'string',
      message: 'Comment is required',
    });
  }
  if (data.comment.length > 5000) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_big,
      path: ['comment'],
      maximum: 5000,
      inclusive: false,
      type: 'string',
      message: 'Comment must be less than 5000 characters',
    });
  }

  // If a reviewer is added, confirmation is not required
  if (formRequirements.isConfirmationRequired && !isApprovalRequestedOrRequired) {
    if (data.resourceConfirmation.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['resourceConfirmation'],
        message: 'Resource confirmation is required',
      });
    }
    const isResourceConfirmationInvalid = isValidResourceConfirmation(
      resourceInfo.resourceKey,
      resourceInfo.resourceName,
    )('resourceConfirmation')({ resourceConfirmation: data.resourceConfirmation });

    if (isResourceConfirmationInvalid) {
      isResourceConfirmationInvalid.errors.forEach((error) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['resourceConfirmation'],
          message: error,
        });
      });
    }
  }

  if (formRequirements.isAcknowledgeConflictsRequired && !data.acknowledgeConflicts) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['acknowledgeConflicts'],
      message: 'Acknowledge conflicts is required',
    });
  }
};

export interface FormRequirements {
  usesLaunchDarklyApprovalService: boolean;
  isConfirmationRequired: boolean;
  isAcknowledgeConflictsRequired: boolean;
  isCommentRequired: boolean;
  isApprovalRequired: boolean;
  minNumberOfApprovals: number;
  canBypassRequiredApprovals: boolean;
}

export interface ResourceInfo {
  resourceKey: string;
  resourceName: string;
}

export const getPendingChangesFormSchema = (params: {
  formRequirements: FormRequirements;
  resourceInfo: ResourceInfo;
}) => {
  const { formRequirements, resourceInfo } = params;

  return pendingChangesFormSchema.superRefine((data, ctx) => {
    return pendingChangesValidation(data, ctx, resourceInfo, formRequirements);
  });
};

export type PendingChangesFormSchema = z.infer<typeof pendingChangesFormSchema>;
